import { isEmpty, isEqual, orderBy } from 'lodash'
import { useState, useEffect, useMemo, useRef } from 'react'
import { connect } from 'react-redux'
import { Container, Row, Col, Button, FormGroup, Label, Input } from 'reactstrap'
import AddressBookModal from './AddressBookModal'
import BillingAddressForm from './BillingAddressForm'
import { setBillingAddress, setUseShippingAsBilling, clearBillingAddress } from './checkoutFunctions'
import { updateCheckoutSessionToken } from '../../redux/storeConfig/slices/slice.checkoutSessionToken/checkoutSessionToken.thunks'

const emptyAddressData = {
  full_name: '',
  title: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  zip_code: '',
  address_phone: '',
  country: 'United States',
  company_name: '',
  country_code: 'US',
  default_shipping: false
}

const BillingAddressSection = ({
  selectedCustomerAddress,
  selectedBillingAddress,
  useShippingAsBilling,
  setBillingAddress,
  setUseShippingAsBilling,
  clearBillingAddress,
  customer_addresses,
  managed_customer_addresses,
  guestAddresses,
  currentUser,
  youPay,
  requireBillingAddressForFreeOrders,
  isDigitalOnly = false,
  checkoutSessionToken,
  updateCheckoutSessionToken
}) => {
  const [showAddressBookModal, setShowAddressBookModal] = useState(false)
  const [billingFormData, setBillingFormData] = useState(selectedBillingAddress || emptyAddressData)
  const prevSelectedBillingRef = useRef(selectedBillingAddress)

  // Get all available billing addresses for the address book
  const billingAddresses = useMemo(() => {
    const traditionalAddresses = customer_addresses || []
    const managedAddresses = Object.values(managed_customer_addresses || {})
    const guest = guestAddresses || []
    const allBillingAddresses = [...traditionalAddresses, ...managedAddresses, ...guest]

    const uniqueBillingAddresses = Object.values(
      allBillingAddresses.reduce((acc, curr) => {
        if (curr?.id) {
          acc[curr.id] = curr
        }
        return acc
      }, {})
    )
    return orderBy(uniqueBillingAddresses, 'id', 'desc')
  }, [customer_addresses, managed_customer_addresses, guestAddresses])

  // Initialize form state when selected billing changes (and not using shipping)
  useEffect(() => {
    const selectedChanged = !isEqual(prevSelectedBillingRef.current, selectedBillingAddress)
    // Always track what we've seen from Redux, regardless of useShippingAsBilling
    if (selectedChanged && !isEmpty(selectedBillingAddress)) {
      prevSelectedBillingRef.current = selectedBillingAddress
      // Only sync form data if not using shipping as billing
      if (!useShippingAsBilling) {
        setBillingFormData(selectedBillingAddress)
      }
    }
  }, [selectedBillingAddress, useShippingAsBilling])

  // When "same as shipping" is checked and shipping changes, sync billing
  useEffect(() => {
    if (useShippingAsBilling && !isEmpty(selectedCustomerAddress)) {
      setBillingAddress(selectedCustomerAddress)
      setBillingFormData(selectedCustomerAddress)
    }
  }, [useShippingAsBilling, selectedCustomerAddress, setBillingAddress])

  const hasShippingAddress = !isEmpty(selectedCustomerAddress)
  const hideBillingSection = youPay === 0 && !requireBillingAddressForFreeOrders

  // Persist billing state to session token
  const persistToSessionToken = (billingAddress, sameAsShipping) => {
    if (!checkoutSessionToken) return
    const isGuestUser = !currentUser?.id

    updateCheckoutSessionToken(checkoutSessionToken, {
      selectedBillingAddress: billingAddress,
      useShippingAsBilling: sameAsShipping,
      selectedCustomerAddressId: isGuestUser ? null : selectedCustomerAddress?.id
    })
  }

  const handleSameAsShippingChange = e => {
    const isChecked = e.target.checked
    setUseShippingAsBilling(isChecked)

    if (isChecked && hasShippingAddress) {
      setBillingAddress(selectedCustomerAddress)
      setBillingFormData(selectedCustomerAddress)
      persistToSessionToken(selectedCustomerAddress, true)
    } else if (!isChecked) {
      clearBillingAddress()
      setBillingFormData(emptyAddressData)
      persistToSessionToken(null, false)
    }
  }

  const handleAddressSelect = address => {
    const sameAsShipping = isEqual(address, selectedCustomerAddress)

    setBillingAddress(address)
    setBillingFormData(address)
    setUseShippingAsBilling(sameAsShipping)
    setShowAddressBookModal(false)
    persistToSessionToken(address, sameAsShipping)
  }

  const handleFormChange = (updatedAddress, options = {}) => {
    // If user edits, turn off "same as shipping"
    if (useShippingAsBilling) {
      setUseShippingAsBilling(false)
    }
    const manualBillingAddress = {
      full_name: updatedAddress.full_name,
      address_line_1: updatedAddress.address_line_1,
      address_line_2: updatedAddress.address_line_2,
      city: updatedAddress.city,
      state: updatedAddress.state,
      zip_code: updatedAddress.zip_code,
      address_phone: updatedAddress.address_phone,
      company_name: updatedAddress.company_name,
      country: updatedAddress.country,
      country_code: updatedAddress.country_code
    }

    setBillingFormData(manualBillingAddress)

    if (options.commit) {
      setBillingAddress(manualBillingAddress)
      persistToSessionToken(manualBillingAddress, false)
    }
  }

  return !hideBillingSection && (
    <div className="box-shadow white-background m-0 mb-3">
      <div className="ckout-header">
        <h5 className="m-0">Billing Address</h5>
      </div>
      <Container className="pb-3">
        <Row className="mb-3 pt-3">
          <Col className="d-flex align-items-center flex-wrap gap-2">
            {!isDigitalOnly && hasShippingAddress && (
              <FormGroup check className="m-0">
                <Label check style={{ cursor: 'pointer', fontSize: '14px' }}>
                  <Input
                    type="checkbox"
                    checked={useShippingAsBilling}
                    onChange={handleSameAsShippingChange}
                    style={{ cursor: 'pointer' }}
                  />
                  Same as Shipping Address Above
                </Label>
              </FormGroup>
            )}

            {currentUser.id && billingAddresses.length > 0 && (
              <Button
                color="link"
                className="p-0 ml-auto"
                onClick={() => setShowAddressBookModal(true)}
                style={{ fontSize: '14px', textDecoration: 'none' }}
              >
                Select from Address Book
              </Button>
            )}
          </Col>
        </Row>

        <Row>
          <Col>
            <BillingAddressForm
              address={billingFormData}
              onAddressChange={handleFormChange}
            />
          </Col>
        </Row>
      </Container>

      <AddressBookModal
        isOpen={showAddressBookModal}
        onClose={() => setShowAddressBookModal(false)}
        addresses={billingAddresses}
        onSelect={handleAddressSelect}
        selectedAddress={selectedBillingAddress}
      />
    </div>
  )
}

const mapStateToProps = state => ({
  selectedCustomerAddress: state.checkout.selectedCustomerAddress,
  selectedBillingAddress: state.checkout.selectedBillingAddress,
  useShippingAsBilling: state.checkout.useShippingAsBilling,
  customer_addresses: state.customer_addresses,
  managed_customer_addresses: state.managed_customer_addresses,
  guestAddresses: state.checkout.guestAddresses,
  currentUser: state.currentUser,
  youPay: state.checkout.youPay,
  requireBillingAddressForFreeOrders: state.storeViews.current.require_billing_address_for_free_orders,
  checkoutSessionToken: state.checkoutSessionToken.token
})

export default connect(mapStateToProps, {
  setBillingAddress,
  setUseShippingAsBilling,
  clearBillingAddress,
  updateCheckoutSessionToken
})(BillingAddressSection)
